import { breakpoint } from '@site/js/utils/breakpoint'

const CAROUSEL_SELECTOR = '[data-n25-carousel]'
const TRACK_SELECTOR = '[data-n25-track]'
const SLIDE_SELECTOR = '[data-n25-slide]'
const PREV_SELECTOR = '[data-n25-prev]'
const NEXT_SELECTOR = '[data-n25-next]'
const DOTS_SELECTOR = '[data-n25-dots]'
const DOT_CLASS = 'n25-carousel__dot'
const ENTERING_CLASS = 'is-entering'
const ACTIVE_SLIDE_CLASS = 'is-active-slide'
const DOCKED_CLASS = 'n25-carousel--docked'
const MOBILE_MQ = breakpoint('down-md')

const SWIPE_THRESHOLD = 40

const pushDataLayer = eventData => {
  window.dataLayer = window.dataLayer || []
  window.dataLayer.push(eventData)
}

const trackBrickView = (index, total) => {
  pushDataLayer({
    event: 'connective_brick_view',
    eventSource: 'aem',
    componentName: 'contentBrickCarousel',
    slideIndex: index,
    slideNumber: index + 1,
    totalSlides: total,
  })
}

const trackBrickNavigation = (fromIndex, toIndex, total) => {
  pushDataLayer({
    event: 'connective_brick_navigation',
    eventSource: 'aem',
    componentName: 'contentBrickCarousel',
    action: 'navigate',
    fromSlide: fromIndex + 1,
    toSlide: toIndex + 1,
    totalSlides: total,
    direction: toIndex > fromIndex ? 'next' : 'prev',
  })
}

const trackNarrativeCompletion = total => {
  pushDataLayer({
    event: 'narrative_completion',
    eventSource: 'aem',
    componentName: 'contentBrickCarousel',
    totalSlides: total,
  })
}

const initCarouselRuntime = root => {
  const track = root.querySelector(TRACK_SELECTOR)
  const slides = Array.from(root.querySelectorAll(SLIDE_SELECTOR))
  const prevBtn = root.querySelector(PREV_SELECTOR)
  const nextBtn = root.querySelector(NEXT_SELECTOR)
  const dotsWrap = root.querySelector(DOTS_SELECTOR)

  if (!track || slides.length === 0) {return}

  let currentIndex = 0
  let maxVisitedIndex = 0
  let lastAnimatedIndex = -1
  let narrativeCompletionFired = false

  const slideWidth = () => slides[0].getBoundingClientRect().width || track.offsetWidth || 1

  const clamp = i => Math.max(0, Math.min(slides.length - 1, i))

  const update = i => {
    if (dotsWrap) {
      Array.from(dotsWrap.querySelectorAll(`.${DOT_CLASS}`)).forEach((dot, idx) => {
        const isActive = idx === i
        const isVisited = !isActive && idx <= maxVisitedIndex
        dot.classList.toggle('is-active', isActive)
        dot.classList.toggle('is-visited', isVisited)
      })
    }
    if (prevBtn) {prevBtn.disabled = i <= 0}
    if (nextBtn) {nextBtn.disabled = i >= slides.length - 1}
  }

  const triggerEnterAnimation = index => {
    if (index === lastAnimatedIndex) { return }
    lastAnimatedIndex = index

    const innerSlide = slides[index].querySelector('.n25-carousel-slide') || slides[index]

    slides.forEach((slide, idx) => {
      const inner = slide.querySelector('.n25-carousel-slide') || slide
      inner.classList.remove(ENTERING_CLASS)

      if (idx !== index && inner.classList.contains(ACTIVE_SLIDE_CLASS)) {
        clearTimeout(inner._leaveTimer)
        inner._leaveTimer = setTimeout(() => inner.classList.remove(ACTIVE_SLIDE_CLASS), 600)
      }
    })

    clearTimeout(innerSlide._leaveTimer)
    void innerSlide.offsetWidth
    innerSlide.classList.add(ACTIVE_SLIDE_CLASS)
    innerSlide.classList.add(ENTERING_CLASS)
  }

  const goTo = (i, isInitial = false) => {
    const previousIndex = currentIndex
    currentIndex = clamp(i)
    if (currentIndex > maxVisitedIndex) {maxVisitedIndex = currentIndex}

    track.scrollTo({ left: currentIndex * slideWidth(), behavior: 'smooth' })
    update(currentIndex)

    trackBrickView(currentIndex, slides.length)

    if (!isInitial) {
      trackBrickNavigation(previousIndex, currentIndex, slides.length)
    }

    triggerEnterAnimation(currentIndex)

    if (!narrativeCompletionFired && currentIndex === slides.length - 1 && slides.length > 1) {
      narrativeCompletionFired = true
      trackNarrativeCompletion(slides.length)
    }
  }

  if (dotsWrap) {
    dotsWrap.innerHTML = ''
    slides.forEach((_, idx) => {
      const seg = document.createElement('button')
      seg.type = 'button'
      seg.className = DOT_CLASS
      seg.setAttribute('aria-label', `Slide ${idx + 1} of ${slides.length}`)
      seg.setAttribute('aria-hidden', 'true')
      seg.setAttribute('tabindex', '-1')
      dotsWrap.appendChild(seg)
    })
  }

  if (prevBtn) {prevBtn.addEventListener('click', () => goTo(currentIndex - 1))}
  if (nextBtn) {nextBtn.addEventListener('click', () => goTo(currentIndex + 1))}

  let isTouching = false

  let scrollTimer
  track.addEventListener('scroll', () => {
    clearTimeout(scrollTimer)
    scrollTimer = setTimeout(() => {
      if (isTouching) {return}
      const snapped = clamp(Math.round(track.scrollLeft / slideWidth()))

      triggerEnterAnimation(snapped)

      if (snapped !== currentIndex) {
        const previousIndex = currentIndex
        currentIndex = snapped
        if (currentIndex > maxVisitedIndex) { maxVisitedIndex = currentIndex }
        update(currentIndex)

        trackBrickView(currentIndex, slides.length)
        trackBrickNavigation(previousIndex, currentIndex, slides.length)

        if (!narrativeCompletionFired && currentIndex === slides.length - 1 && slides.length > 1) {
          narrativeCompletionFired = true
          trackNarrativeCompletion(slides.length)
        }
      }
    }, 50)
  })

  let resizeTimer
  window.addEventListener('resize', () => {
    clearTimeout(resizeTimer)
    resizeTimer = setTimeout(() => {
      track.scrollTo({ left: currentIndex * slideWidth(), behavior: 'auto' })
      update(currentIndex)
      updateControlsDock()
    }, 150)
  })

  root.addEventListener('keydown', e => {
    if (e.key === 'ArrowLeft') { e.preventDefault(); goTo(currentIndex - 1) }
    if (e.key === 'ArrowRight') { e.preventDefault(); goTo(currentIndex + 1) }
  })
  track.style.touchAction = 'pan-y'

  let touchStartX = 0
  let touchStartY = 0
  let touchStartScrollLeft = 0
  let isDragging = false

  track.addEventListener('touchstart', e => {
    const touch = e.changedTouches[0]
    touchStartX = touch.clientX
    touchStartY = touch.clientY
    touchStartScrollLeft = track.scrollLeft
    isDragging = true
    isTouching = true
  }, { passive: true })

  track.addEventListener('touchmove', e => {
    if (!isDragging) { return }
    const dx = touchStartX - e.changedTouches[0].clientX
    track.scrollLeft = touchStartScrollLeft + dx
  }, { passive: true })

  track.addEventListener('touchend', e => {
    if (!isDragging) {
      isTouching = false
      return
    }
    isDragging = false

    const touch = e.changedTouches[0]
    const dx = touch.clientX - touchStartX
    const dy = touch.clientY - touchStartY

    if (Math.abs(dy) > Math.abs(dx) || Math.abs(dx) < SWIPE_THRESHOLD) {
      track.scrollTo({ left: currentIndex * slideWidth(), behavior: 'smooth' })
      setTimeout(() => { isTouching = false }, 500)
      return
    }

    const startIndex = clamp(Math.round(touchStartScrollLeft / slideWidth()))
    goTo(dx < 0 ? startIndex + 1 : startIndex - 1)
    setTimeout(() => { isTouching = false }, 500)
  }, { passive: true })

  track.addEventListener('touchcancel', () => {
    if (isDragging) {
      track.scrollTo({ left: currentIndex * slideWidth(), behavior: 'smooth' })
    }
    isDragging = false
    isTouching = false
  }, { passive: true })

  // Initial state
  goTo(0, true)

  const controlsEl = root.querySelector('.n25-carousel__controls')

  const updateControlsDock = () => {
    if (!controlsEl) { return }
    if (!MOBILE_MQ.matches) {
      root.classList.remove(DOCKED_CLASS)
      return
    }
    const rect = root.getBoundingClientRect()
    root.classList.toggle(DOCKED_CLASS, rect.bottom <= window.innerHeight)
  }

  window.addEventListener('scroll', updateControlsDock, { passive: true })
  updateControlsDock()
}

const initCarousel = carousel => {
  if (!('IntersectionObserver' in window)) {
    initCarouselRuntime(carousel)
    return
  }

  const observer = new IntersectionObserver(
    (entries, obs) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          initCarouselRuntime(entry.target)
          obs.unobserve(entry.target)
        }
      })
    },
    { rootMargin: '0px 0px 200px 0px' }
  )

  observer.observe(carousel)
}

const initAll = () => {
  const carousels = document.querySelectorAll(CAROUSEL_SELECTOR)
  if (carousels?.length) {
    carousels.forEach(initCarousel)
  }
}

document.addEventListener('DOMContentLoaded', () => initAll())
